import { getClientContext, getTelemetryHostingConfig } from '@ms/yammer-telemetry-store';
import { getNetworkInformation } from '@ms/yammer-telemetry-support';

import type {
  AnalyticsEvent,
  AnalyticsEventInput,
  ErrorEvent,
  LogEvent,
  LogEventWithOptions,
  PerformanceEvent,
  TelemetryProperties,
} from '../events';

import { translateLegacyEventsProperties } from './translateLegacyEventsProperties';

const getErrorEventSpecificProperties = ({ message, stack }: ErrorEvent) => ({
  ...getTelemetryHostingConfig(),
  message,
  stack,
});

const getAnalyticsEventSpecificProperties = () => ({
  scriptRevision: getTelemetryHostingConfig()?.scriptRevision ?? '',
});

const getPerformanceEventSpecificProperties = ({ startTime, duration }: PerformanceEvent) => {
  const { downlink, downlinkMax, effectiveType, rtt, saveData, type } = getNetworkInformation();

  const endTime = typeof startTime === 'number' && typeof duration === 'number' ? duration + startTime : undefined;

  return {
    ...getTelemetryHostingConfig(),
    connectionDownlink: downlink,
    connectionDownlinkMax: downlinkMax,
    connectionEffectiveType: effectiveType,
    connectionRtt: rtt,
    connectionSaveData: saveData,
    connectionType: type,
    duration,
    startTime,
    endTime,
  };
};

const getLogEventSpecificProperties = (event: LogEvent) => {
  switch (event.type) {
    case 'Error':
      return getErrorEventSpecificProperties(event);
    case 'Info':
      return getTelemetryHostingConfig();
    default:
      return getPerformanceEventSpecificProperties(event);
  }
};

const getTelemetryEventProperties = (): TelemetryProperties => {
  const {
    deviceType,
    hostClientType,
    osName,
    clientFeedLayout,
    hostAppName,
    isPWA,
    isMultiWindow,
    hostSessionId,
    hostRingId,
    hostLaunchId,
    appSessionId,
    appManifestVersion,
  } = getClientContext();

  return {
    ...(deviceType && { deviceType: deviceType.toLowerCase() }),
    ...(hostClientType && { hostClientType: hostClientType.toLowerCase() }),
    ...(hostAppName && { hostAppName: hostAppName.toLowerCase() }),
    ...(osName && { OsName: osName.toLowerCase() }),
    ...(clientFeedLayout && { clientFeedLayout: clientFeedLayout.toLowerCase() }),
    ...(isPWA && { isPWA }),
    ...(isMultiWindow && { isMultiWindow }),
    ...(hostSessionId && { hostSessionId }),
    ...(hostRingId && { ringId: hostRingId }),
    ...(hostLaunchId && { hostLaunchId }),
    ...(appSessionId && { appSessionId }),
    ...(appManifestVersion && { appManifestVersion }),
  };
};

type GetAnalyticsEventInput = (event: AnalyticsEvent) => AnalyticsEventInput;

export const getAnalyticsEventInput: GetAnalyticsEventInput = (analyticsEvent) => ({
  name: analyticsEvent.name,
  properties: {
    ...getAnalyticsEventSpecificProperties(),
    ...translateLegacyEventsProperties(analyticsEvent),
    ...getTelemetryEventProperties(),
    client_event_time: analyticsEvent.occurredAt,
  },
});

type GetLogEventInput = (event: LogEvent) => LogEventWithOptions;
export const getLogEventInput: GetLogEventInput = (event) => ({
  event: {
    EventName: event.name,
    ClientTimeStamp: Number(event.occurredAt),
    LogType: event.type.toLowerCase(),
    Parameters: {
      ...getLogEventSpecificProperties(event),
      ...event.properties,
      ...getTelemetryEventProperties(),
      client_event_time: event.occurredAt,
    },
  },
  options: { skipAuthentication: event.skipAuthentication },
});
